import api, { apiWithRetry } from './api';

/**
 * Important Date service for fetching important dates from Strapi API
 */

export const importantDateService = {
  /**
   * Get all important dates
   * @param {Object} options - Query options
   * @param {string} options.sort - Sort order (default: 'date:asc')
   * @param {string} options.populate - Relations to populate (default: '*')
   * @returns {Promise<Object>} Important dates data
   */
  getImportantDates: async (options = {}) => {
    try {
      const {
        sort = 'date:asc',
        populate = '*',
      } = options;

      const params = {
        'sort': sort,
        'populate': populate,
      };
      
      const response = await apiWithRetry(() => 
        api.get('/api/important-dates', { params })
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching important dates:', error);
      throw new Error(error.userMessage || 'Failed to fetch important dates');
    }
  },

  /**
   * Get upcoming important dates
   * @param {number} limit - Maximum number of dates to return (default: 5)
   * @returns {Promise<Object>} Upcoming important dates data
   */
  getUpcomingDates: async (limit = 5) => {
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      
      const params = {
        'filters[date][$gte]': today,
        'sort': 'date:asc',
        'pagination[pageSize]': limit,
        'populate': '*',
      };
      
      const response = await apiWithRetry(() => 
        api.get('/api/important-dates', { params })
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching upcoming important dates:', error);
      throw new Error(error.userMessage || 'Failed to fetch upcoming important dates');
    }
  },

  /**
   * Get important dates by conference
   * @param {string} conferenceSlug - Conference slug
   * @returns {Promise<Object>} Important dates data
   */
  getDatesByConference: async (conferenceSlug) => {
    try {
      const params = {
        'filters[relatedConference][slug][$eq]': conferenceSlug,
        'sort': 'date:asc',
        'populate': '*',
      };
      
      const response = await apiWithRetry(() => 
        api.get('/api/important-dates', { params })
      );
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching important dates for conference ${conferenceSlug}:`, error);
      throw new Error(error.userMessage || 'Failed to fetch important dates');
    }
  },

  /**
   * Get important date by ID
   * @param {number} id - Important date ID
   * @param {string} populate - Relations to populate (default: '*')
   * @returns {Promise<Object>} Important date data
   */
  getImportantDateById: async (id, populate = '*') => {
    try {
      const params = { populate };
      
      const response = await apiWithRetry(() => 
        api.get(`/api/important-dates/${id}`, { params })
      );
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching important date ${id}:`, error);
      throw new Error(error.userMessage || 'Failed to fetch important date');
    }
  },
};

export default importantDateService;
